import { trackProductView } from "../../hooks/useAnalyticsTracking";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useEffect, useRef } from "react";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import { useProductBySlug, useProducts } from "../../hooks/useProductData";
import { getProductUrl, createSlug } from "../../lib/slug";
import ProductHero from "./components/ProductHero";
import ProductOverview from "./components/ProductOverview";
import ProductScrollStory from "./components/ProductScrollStory";
import ProductApplicationSection from "./components/ProductApplicationSection";
import ProductPatentSection from "./components/ProductPatentSection";
import ProductConsultation from "./components/ProductConsultation";
import BuilderRenderer from "./components/BuilderRenderer";
import CatalogView from "./components/CatalogView";
import { useScrollReveal } from "../../hooks/useScrollReveal";
import { BuilderPage } from "../admin/components/builder/types";
import SEO from "@/components/SEO";
import { ProductSchema, BreadcrumbSchema } from "@/components/StructuredData";

// ── Loading skeleton ──────────────────────────────────────────────────────────
const ProductSkeleton = () => (
  <div className="min-h-screen bg-white">
    <Navbar />
    <div className="w-full h-screen bg-gray-100 animate-pulse" />
    <div className="max-w-[1400px] mx-auto px-8 py-20 grid lg:grid-cols-2 gap-12">
      <div className="space-y-4">
        <div className="h-6 w-1/3 bg-gray-200 rounded-lg animate-pulse" />
        <div className="h-10 w-2/3 bg-gray-200 rounded-lg animate-pulse" />
        <div className="h-4 w-full bg-gray-100 rounded-lg animate-pulse" />
        <div className="h-4 w-5/6 bg-gray-100 rounded-lg animate-pulse" />
      </div>
      <div className="h-72 bg-gray-100 rounded-2xl animate-pulse" />
    </div>
    <Footer />
  </div>
);

// ── Related products ──────────────────────────────────────────────────────────
const RelatedProducts = ({
  categoryId,
  currentId,
}: {
  categoryId: string;
  currentId: string;
}) => {
  const { ref, isVisible } = useScrollReveal(0.1);
  const { products: allProducts } = useProducts();
  const related = allProducts
    .filter((p) => p.categoryId === categoryId && p.id !== currentId)
    .slice(0, 3);

  if (related.length === 0) return null;

  return (
    <section className="py-20 bg-[#F8FAFE]" aria-label="Sản phẩm liên quan">
      <div className="max-w-[1400px] mx-auto px-8">
        <div
          ref={ref}
          className={`transition-all duration-700 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <p className="text-xs font-bold tracking-[0.25em] text-[#004A99] uppercase mb-3">
            Có Thể Bạn Quan Tâm
          </p>
          <h2 className="text-3xl font-bold text-gray-900 mb-10">Sản Phẩm Liên Quan</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {related.map((p, i) => (
              <Link
                key={p.id}
                to={getProductUrl({ companyName: p.companyName ?? "", name: p.name })}
                style={{ transitionDelay: `${i * 100}ms` }}
                className="group bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-[#004A99]/30 transition-all cursor-pointer block"
                rel="noopener"
              >
                <div className="h-52 overflow-hidden bg-gray-50">
                  <img
                    src={p.images[0]}
                    alt={`${p.name} - ${p.companyName}`}
                    className="w-full h-full object-contain bg-gray-50 group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                </div>
                <div className="p-5">
                  <span className="text-xs bg-[#EEF4FF] text-[#004A99] px-2 py-0.5 rounded-full mb-2 inline-block">
                    {p.subCategoryName}
                  </span>
                  <h3 className="font-bold text-gray-900 text-sm line-clamp-2 mb-1 group-hover:text-[#004A99] transition-colors leading-snug">
                    {p.name}
                  </h3>
                  <p className="text-xs text-gray-400 mb-3">{p.companyName}</p>
                  <p className="text-xs font-semibold text-[#004A99]">{p.price}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────
const ProductDetailPage = () => {
  const { companySlug, productSlug } = useParams<{ companySlug: string; productSlug: string }>();
  const [searchParams] = useSearchParams();
  const isCatalogMode = searchParams.get("view") === "catalog";
  const { product, loading } = useProductBySlug(companySlug, productSlug);
  const consultationRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (product && companySlug && productSlug) {
      trackProductView(
        product.id,
        product.name,
        companySlug,
        productSlug,
        window.location.pathname + window.location.search
      );
    }
  }, [product?.id, companySlug, productSlug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [companySlug, productSlug]);

  const handleInquiryClick = () => {
    const el = document.getElementById("inquiry");
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  if (!loading && product && isCatalogMode) {
    return <CatalogView product={product} />;
  }

  if (loading) return <ProductSkeleton />;

  if (!product) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <main className="flex items-center justify-center min-h-[70vh]">
          <div className="text-center">
            <i className="ri-error-warning-line text-5xl text-gray-200 block mb-5" aria-hidden="true" />
            <p className="text-gray-500 text-lg font-medium mb-2">Không tìm thấy sản phẩm</p>
            <Link
              to="/danh-muc"
              className="mt-4 inline-flex items-center gap-2 text-[#004A99] hover:text-[#003580] cursor-pointer text-sm font-medium"
            >
              <i className="ri-arrow-left-line" aria-hidden="true" />
              Quay lại danh mục
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const builderPages = ((product as unknown as Record<string, unknown>).builder_pages ?? []) as BuilderPage[];
  const hasBuilderContent = builderPages.length > 0 && builderPages.some((p) => p.blocks.length > 0);

  // eslint-disable-next-line no-console
  console.debug("[ProductPage] product.id=", product?.id, "| display_mode=", product?.display_mode, "| builderPages.length=", builderPages.length, "| hasBuilderContent=", hasBuilderContent);

  const productImage = (() => {
    const rawMedia = (product as unknown as Record<string, unknown>).media_items;
    const mediaItems = Array.isArray(rawMedia) ? rawMedia as Array<{ url: string; type: string; is_cover?: boolean }> : [];
    const cover = mediaItems.find((m) => m.is_cover && m.type === "image");
    const first = mediaItems.find((m) => m.type === "image");
    return cover?.url ?? first?.url ?? product.images[0] ?? "";
  })();

  const siteUrl = typeof window !== "undefined" ? `${window.location.protocol}//${window.location.host}` : "";

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={`${product.name} – ${product.companyName || "Sản phẩm Hàn Quốc"}`}
        description={product.description ? `${product.description.slice(0, 155)}...` : `${product.name} của ${product.companyName}. Sản phẩm công nghệ tiên tiến từ Hàn Quốc – tìm hiểu chi tiết và liên hệ tư vấn tại KCANIA.`}
        keywords={`${product.name}, ${product.companyName}, ${product.categoryName}, ${product.subCategoryName}, sản phẩm Hàn Quốc`}
        ogImage={productImage}
        ogType="product"
      />
      <ProductSchema
        name={product.name}
        description={product.description}
        image={productImage}
        brand={product.companyName}
        sku={product.id}
        offers={{
          price: product.price || "Liên hệ",
          priceCurrency: "VND",
          availability: "https://schema.org/InStock",
          seller: product.companyName,
        }}
      />
      <BreadcrumbSchema
        items={[
          { name: "Trang chủ", url: siteUrl || "/" },
          { name: "Danh mục", url: `${siteUrl}/danh-muc` },
          ...(product.categoryName ? [{ name: product.categoryName, url: `${siteUrl}/danh-muc` }] : []),
          { name: product.name, url: window.location.href },
        ]}
      />

      <Navbar />

      <article itemScope itemType="https://schema.org/Product">
        <meta itemProp="name" content={product.name} />
        {productImage && <meta itemProp="image" content={productImage} />}
        {product.description && <meta itemProp="description" content={product.description} />}
        {product.companyName && (
          <div itemProp="brand" itemScope itemType="https://schema.org/Brand" className="hidden">
            <meta itemProp="name" content={product.companyName} />
          </div>
        )}

        <ProductHero product={product} />
        <ProductOverview product={product} onInquiryClick={handleInquiryClick} />

        <section aria-label="Thông tin chi tiết sản phẩm">
          {hasBuilderContent && (
            <div id="builder-content">
              <BuilderRenderer pages={builderPages} productName={product.name} />
            </div>
          )}
          <ProductScrollStory product={product} />
          <ProductApplicationSection product={product} />
          <ProductPatentSection
            patents={product.patents_certifications ?? []}
            productName={product.name}
          />
        </section>

        <section aria-label="Liên hệ tư vấn" id="inquiry">
          <ProductConsultation product={product} formRef={consultationRef} />
        </section>
      </article>

      <RelatedProducts categoryId={product.categoryId} currentId={product.id} />

      {/* Internal Linking: Related categories */}
      <nav aria-label="Khám phá thêm danh mục" className="bg-[#F8F9FA] border-t border-gray-100 py-6">
        <div className="max-w-[1400px] mx-auto px-8">
          <p className="text-xs text-gray-400 mb-3 font-semibold uppercase tracking-wider">Khám phá thêm</p>
          <div className="flex flex-wrap gap-2">
            <Link to="/danh-muc" className="text-xs bg-white border border-gray-200 hover:border-[#004A99]/30 text-gray-600 hover:text-[#004A99] px-3 py-1.5 rounded-lg transition-colors">
              Tất cả danh mục
            </Link>
            <Link to="/doanh-nghiep" className="text-xs bg-white border border-gray-200 hover:border-[#004A99]/30 text-gray-600 hover:text-[#004A99] px-3 py-1.5 rounded-lg transition-colors">
              Doanh nghiệp Hàn Quốc
            </Link>
            {product.categoryName && (
              <Link to="/danh-muc" className="text-xs bg-white border border-gray-200 hover:border-[#004A99]/30 text-gray-600 hover:text-[#004A99] px-3 py-1.5 rounded-lg transition-colors">
                {product.categoryName}
              </Link>
            )}
            <Link to="/hop-tac-ky-thuat" className="text-xs bg-white border border-gray-200 hover:border-[#004A99]/30 text-gray-600 hover:text-[#004A99] px-3 py-1.5 rounded-lg transition-colors">
              Hợp tác kỹ thuật
            </Link>
          </div>
        </div>
      </nav>

      <Footer />
    </div>
  );
};

export default ProductDetailPage;
